import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { Row, Col, Label, FormGroup, Button } from "reactstrap";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Accordion } from "react-bootstrap";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { TbLoader } from "react-icons/tb";
import CustomizationService from "src/modules/3rd-party-management/apis/admin/CustomizationService";
import IntroImage from "src/assets/images/intro.svg";
import { reloadOrganizationActiveModuleSettingsFromServer } from "src/store/actions";
import { Editor } from "react-draft-wysiwyg";
import {
  ContentState,
  EditorState,
  convertFromHTML,
  convertToRaw,
} from "draft-js";
import EmailIntroModal from "./email-intro-modal";
import draftToHtml from "draftjs-to-html";

const TPDDEmailIntroCustomization = (props) => {
  const {
    t,
    fetchOrganizationDetails,
    reloadActiveModuleSettings,
    organization,
  } = props;
  const details = fetchOrganizationDetails.data;
  const [defaultLanguage, setDefaultLanguage] = useState(null);
  const [defaultTranslation, setDefaultTranslation] = useState();
  const [translations, setTranslations] = useState(
    details?.mail_intro_translations
  );
  const [introModal, setIntroModal] = useState(false);
  const [langToDisplayOnModal, setLangToDisplayOnModal] = useState(null);

  /*********************************** FUNCTIONS ***************************************** */

  // Submit Information
  const handleSubmitMutation = useMutation({
    mutationFn: async () => {
      if (!defaultTranslation || !defaultTranslation.editorState) {
        throw new Error("EditorState is not defined");
      }

      const rawContentState = convertToRaw(
        defaultTranslation.editorState.getCurrentContent()
      );
      const introHtml = draftToHtml(rawContentState);
      const payload = {
        text: introHtml,
        language: defaultTranslation.lang,
      };

      const service = CustomizationService.getInstance();
      return await service.editEmailTranslation(payload);
    },
    onSuccess: () => {
      reloadActiveModuleSettings();
      toast(t("Data saved successfully."), {
        type: "success",
      });
    },
    onError: () => {
      toast(t("Failed to save data."), {
        type: "error",
      });
    },
  });

  const restore = () => {
    setTranslations(details?.mail_intro_translations);
    resetDefaultTranslation();
  };

  // Font and Set Default Language and Translation
  const findDefaultLanguage = () => {
    const { mail_intro_translations } = fetchOrganizationDetails.data;
    const defLang = mail_intro_translations.filter(
      (translation) => translation?.is_default
    )[0];

    let contentState;
    if (defLang.text) {
      const blocksFromHTML = convertFromHTML(defLang.text);
      contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
    } else {
      contentState = ContentState.createFromText("");
    }

    setDefaultTranslation({
      ...defLang,
      editorState: EditorState.createWithContent(contentState),
    });

    // setDefaultPageTitle(defLang?.title);
    const defLangCode = defLang.lang;
    const defLangTitle = organization.languages.filter(
      (lang) => lang.language_code === defLangCode
    )[0]?.name;
    setDefaultLanguage(defLangTitle);
  };

  // On Editor State Change
  const handleEditorStateChange = (editorState) => {
    setDefaultTranslation((currentTrans) => ({
      ...currentTrans,
      editorState,
    }));
  };

  // Reset Default Translation
  const resetDefaultTranslation = () => {
    let contentState;
    const { intro } = defaultTranslation;
    if (intro) {
      const blocksFromHTML = convertFromHTML(intro);
      contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
    } else {
      contentState = ContentState.createFromText("");
    }

    setDefaultTranslation({
      ...defaultTranslation,
      editorState: EditorState.createWithContent(contentState),
    });
  };

  /*************************************** USE EFFECTS ****************************************** */
  useEffect(() => {
    if (fetchOrganizationDetails.data?.mail_intro_translations) {
      findDefaultLanguage();
    }
  }, [fetchOrganizationDetails.data]);
  /*************************************** VIEW ****************************************** */
  return (
    <Row>
      {introModal && (
        <EmailIntroModal
          modal={introModal}
          setModal={setIntroModal}
          fetchOrganizationDetails={fetchOrganizationDetails}
          translation={langToDisplayOnModal}
          closeModal={() => {
            setIntroModal(false);
            setLangToDisplayOnModal(null);
          }}
        />
      )}
      <div className="page-tpdd-customization">
        <Row>
          <Col className="d-flex align-items-start customization-image-col">
            <img src={IntroImage} alt="Intro" />
          </Col>
          <Col className="customization-accordion-col">
            <Accordion defaultActiveKey="0">
              <Accordion.Item
                className="border-none cusom-accordion-item"
                eventKey="0"
              >
                <Accordion.Header className="custom-accordion-header">
                  <div className="p-1 custom-div-header collapsed">
                    {`7. ${t("Email Intro")}`}{" "}
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Label
                    style={{
                      width: "228px",
                      height: "28px",
                      gap: "0px",
                      opacity: "0px",
                      display: "block",
                    }}
                  >
                    {t("Intro And Captions")}
                  </Label>
                  
                  <Row className="mt-5">
                    <h4>{t("Default Language") + ": " + t(defaultLanguage)}</h4>
                  </Row>
                  <Row>
                    <Col>
                      <Label for="pageTitle">{t("Intro Text") + ":"}</Label>
                      {defaultTranslation && (
                        <Editor
                          toolbarClassName="toolbarClassName"
                          wrapperClassName="wrapperClassName"
                          editorClassName="editorClassName"
                          editorState={defaultTranslation.editorState}
                          onEditorStateChange={handleEditorStateChange}
                          stripPastedStyles={true}
                        />
                      )}
                    </Col>
                  </Row>

                  <Row hidden={!translations?.length}>
                    <Col className="mt-3">
                      <FormGroup>
                        <Label for="translationLanguages">
                          {t("Translation Languages") + ":"}
                        </Label>
                        <Row>
                          {translations
                            .filter((trans) => !trans.is_default)
                            ?.map((translation, index) => {
                              return index % 2 === 0 ? (
                                <Col
                                  lg="6"
                                  md="6"
                                  sm="12"
                                  className="d-flex mb-3"
                                >
                                  <Col lg="3" md="3" sm="12">
                                    <div className="langButton d-flex justify-content-center">
                                      {t(translation.language_name)}
                                    </div>
                                  </Col>
                                  <Col
                                    lg="3"
                                    md="3"
                                    className="ms-3 d-flex align-items-end"
                                  >
                                    <Label
                                      className="text-primary"
                                      style={{ cursor: "pointer" }}
                                      onClick={() => {
                                        setLangToDisplayOnModal(translation);
                                        setIntroModal(true);
                                      }}
                                    >
                                      {t("View And Edit")}
                                    </Label>
                                  </Col>
                                </Col>
                              ) : (
                                <Col
                                  lg="6"
                                  md="6"
                                  sm="12"
                                  className="d-flex mb-3"
                                >
                                  <Col lg="3" md="3" sm="12">
                                    <div className="langButton d-flex justify-content-center">
                                      {t(translation.language_name)}
                                    </div>
                                  </Col>
                                  <Col
                                    lg="3"
                                    md="3"
                                    className="ms-3 d-flex align-items-end"
                                  >
                                    <Label
                                      className="text-primary"
                                      style={{ cursor: "pointer" }}
                                      onClick={() => {
                                        setLangToDisplayOnModal(translation);
                                        setIntroModal(true);
                                      }}
                                    >
                                      {t("View And Edit")}
                                    </Label>
                                  </Col>
                                </Col>
                              );
                            })}
                        </Row>
                      </FormGroup>
                    </Col>
                  </Row>
                </Accordion.Body>
                <Row className="p-4">
                  <Col sm="12" className="d-flex justify-content-end">
                    <Button
                      onClick={restore}
                      color="primary"
                      type="button"
                      outline
                      className="me-2"
                      disabled={
                        handleSubmitMutation.isLoading ||
                        fetchOrganizationDetails.isLoading ||
                        fetchOrganizationDetails.isFetching
                      }
                    >
                      {t("Restore")}
                    </Button>

                    <Button
                      color="primary"
                      onClick={handleSubmitMutation.mutate}
                      type="submit"
                      disabled={
                        handleSubmitMutation.isLoading ||
                        fetchOrganizationDetails.isLoading ||
                        fetchOrganizationDetails.isFetching
                      }
                    >
                      {handleSubmitMutation.isLoading ? <TbLoader /> : t("Save")}
                    </Button>
                  </Col>
                </Row>
              </Accordion.Item>
            </Accordion>
          </Col>
        </Row>
      </div>
    </Row>
  );
};

const mapStatetoProps = (state) => {
  const { token } = state.Login;
  const organization = state.Organization;
  const App = state.App;
  const modules = state.Modules;
  return { organization, token, App, modules };
};

const mapDispachToProps = (dispach) => {
  return {
    reloadActiveModuleSettings: () =>
      dispach(reloadOrganizationActiveModuleSettingsFromServer()),
  };
};

export default withNamespaces()(
  withRouter(
    connect(mapStatetoProps, mapDispachToProps)(TPDDEmailIntroCustomization)
  )
);
